import React, { useMemo, useRef } from 'react';
import { DRAG_THRESHOLD, capture, isMultiKey, isTypingTarget, swallowNextClick } from './events';
import { flowToScreen, nodesInRect, rectFromPoints, screenToFlow } from './geometry';
import type { Box, XY } from './geometry';
import type { CanvasStoreBundle } from './canvasStore';
import type { ViewportApi } from './useViewport';

interface Options {
  /** May nudge this node: a manager, or its authority, on a writable plan. */
  canMove: (id: string) => boolean;
  /** Everything a group holds: a nudged group carries it. */
  descendantsOf: (id: string) => string[];
  /** May remove this arrow: a manager, or the authority on either end. */
  canRemoveEdge: (id: string) => boolean;
  onDeleteEdges: (ids: string[]) => void;
  onSavePositions: (ids: string[]) => void;
}

const NUDGE = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] } as const;
type ArrowKey = keyof typeof NUDGE;
const isArrow = (key: string): key is ArrowKey => key in NUDGE;

export interface SelectionApi {
  /** True when this pointerdown started a marquee (Shift on empty space). */
  onPointerDown: (e: React.PointerEvent) => boolean;
  onPointerMove: (e: React.PointerEvent) => boolean;
  onPointerUp: (e: React.PointerEvent) => boolean;
  clickNode: (id: string, e: { ctrlKey: boolean; metaKey: boolean }) => void;
  clickEdge: (id: string, e: { ctrlKey: boolean; metaKey: boolean }) => void;
  clickPane: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onKeyUp: (e: React.KeyboardEvent) => void;
}

export function useSelection(bundle: CanvasStoreBundle, viewport: ViewportApi, opts: Options): SelectionApi {
  const { store } = bundle;
  const latest = useRef(opts);
  latest.current = opts;
  const marquee = useRef<{ id: number; startFlow: XY; startScreen: XY; moved: boolean } | null>(null);
  const nudged = useRef(new Set<string>());

  const toggle = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };

  const clickNode: SelectionApi['clickNode'] = (id, e) => {
    const s = store.getState();
    s.selectNodes(isMultiKey(e) ? toggle(s.selection, id) : [id]);
  };

  const clickEdge: SelectionApi['clickEdge'] = (id, e) => {
    const s = store.getState();
    s.selectEdges(isMultiKey(e) ? toggle(s.edgeSelection, id) : [id]);
  };

  const clickPane = () => store.getState().clearSelection();

  const onPointerDown = (e: React.PointerEvent) => {
    // The marquee is a mouse or pen gesture; one finger on empty space always pans.
    if (!e.shiftKey || e.pointerType === 'touch' || e.button !== 0) return false;
    const p = viewport.panePoint(e.clientX, e.clientY);
    marquee.current = { id: e.pointerId, startFlow: screenToFlow(p, viewport.vp.current), startScreen: p, moved: false };
    return true;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const m = marquee.current;
    if (!m || e.pointerId !== m.id) return false;
    const p = viewport.panePoint(e.clientX, e.clientY);
    if (!m.moved) {
      if (Math.hypot(p.x - m.startScreen.x, p.y - m.startScreen.y) < DRAG_THRESHOLD) return true;
      m.moved = true;
      capture(e.currentTarget, e.pointerId);
    }
    const vp = viewport.vp.current;
    // The start is kept in flow units, so the box stays anchored to the content.
    const screenRect = rectFromPoints(flowToScreen(m.startFlow, vp), p);
    const flowRect = rectFromPoints(m.startFlow, screenToFlow(p, vp));
    const measured: Record<string, Box> = {};
    const groups: string[] = [];
    const { nodes } = store.getState();
    Object.keys(nodes).forEach((id) => {
      const b = nodes[id];
      if (!b.measured) return;
      if (!b.group) measured[id] = b;
      // A group joins only when the marquee holds all of it: a marquee drawn inside a bubble selects cards, not the bubble.
      else if (b.x >= flowRect.x && b.y >= flowRect.y && b.x + b.width <= flowRect.x + flowRect.width && b.y + b.height <= flowRect.y + flowRect.height) {
        groups.push(id);
      }
    });
    store.setState({ marquee: screenRect });
    // selectNodes only writes when the set actually changes.
    store.getState().selectNodes([...nodesInRect(measured, flowRect), ...groups]);
    return true;
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const m = marquee.current;
    if (!m || e.pointerId !== m.id) return false;
    marquee.current = null;
    store.setState({ marquee: null });
    if (m.moved) swallowNextClick();
    return true;
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (isTypingTarget(e.target)) return;
    const s = store.getState();
    if (e.key === 'Escape') {
      if (s.connecting) return;
      s.clearSelection();
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      // Arrows only: nodes are deleted from the document view or the side panel, on purpose.
      const removable = Array.from(s.edgeSelection).filter(latest.current.canRemoveEdge);
      if (removable.length === 0) return;
      e.preventDefault();
      latest.current.onDeleteEdges(removable);
      return;
    }
    const carried = new Set<string>();
    if (isArrow(e.key)) {
      s.selection.forEach((id) => {
        carried.add(id);
        latest.current.descendantsOf(id).forEach((c) => carried.add(c));
      });
    }
    const movable = Array.from(carried).filter(latest.current.canMove);
    if (isArrow(e.key) && movable.length > 0) {
      e.preventDefault();
      const step = e.shiftKey ? 50 : 10;
      const [dx, dy] = NUDGE[e.key];
      const moves: Record<string, XY> = {};
      movable.forEach((id) => {
        const b = s.nodes[id];
        if (!b) return;
        moves[id] = { x: b.x + dx * step, y: b.y + dy * step };
        nudged.current.add(id);
      });
      bundle.beginMove(Object.keys(moves));
      s.moveNodes(moves);
    }
  };

  const onKeyUp = (e: React.KeyboardEvent) => {
    if (!isArrow(e.key) || nudged.current.size === 0) return;
    // Held arrows repeat keydown; the save waits for the key to come up.
    const ids = Array.from(nudged.current);
    nudged.current.clear();
    bundle.endMove(ids);
    latest.current.onSavePositions(ids);
  };

  // Handlers read the store and refs only; one set serves every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => ({ onPointerDown, onPointerMove, onPointerUp, clickNode, clickEdge, clickPane, onKeyDown, onKeyUp }), [
    bundle,
    viewport,
  ]);
}
